"use strict";
/**
 * `ModerationMode` enum, its Zod schema, and the pure helpers translating
 * between the mode and an instance's feature flags. Lives in shared so both
 * client and server can dispatch on the active moderation mode, and so the
 * silo's permitted-modes setting and a space's active mode validate against
 * one enum.
 *
 * The mode is a first-class field on the space resource (`spaces.get` returns
 * it, `spaces.create` / `spaces.update` take it), but per-instance storage
 * still keeps it as one feature key per mode. The server owns that
 * translation: `getCurrentModerationSetting` reads the stored flags,
 * `getFeaturesForModerationMode` writes the patch that turns on exactly one
 * mode and turns the others off.
 *
 * Client-only pieces (silo-setting helpers, UI labels, image map) stay in
 * `client/util/moderation-modes.ts`.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.MODERATION_MODE_UI_ORDER = exports.moderationModeSchema = exports.ModerationMode = void 0;
exports.getDefaultModerationMode = getDefaultModerationMode;
exports.getCurrentModerationSetting = getCurrentModerationSetting;
exports.getFeaturesForModerationMode = getFeaturesForModerationMode;
const z = __importStar(require("zod"));
var ModerationMode;
(function (ModerationMode) {
    ModerationMode["AI_MODERATION"] = "ai_moderation";
    ModerationMode["AI_AND_MANUAL_MODERATION"] = "ai_and_manual_moderation";
    ModerationMode["MANUAL_MODERATION"] = "manual_moderation";
    ModerationMode["AI_MODERATION_WITH_POST_ANYWAY"] = "ai_moderation_with_post_anyway";
})(ModerationMode || (exports.ModerationMode = ModerationMode = {}));
exports.moderationModeSchema = z.enum(ModerationMode);
/**
 * The four modes in the order the UI presents them, per
 * https://www.figma.com/design/5vqFJlKdA6UAQJeXYlMQW2/PSI-Stage-2?node-id=9672-63344&t=nS2JLDeShLQnYaKG-0
 *
 * Load-bearing, and not the enum's declaration order: it decides which mode
 * `getDefaultModerationMode` picks for a new space, and which one wins in
 * `getCurrentModerationSetting` if an instance somehow has two enabled. The
 * client's `moderationModeOptions` renders in this order too.
 */
exports.MODERATION_MODE_UI_ORDER = [
    ModerationMode.AI_MODERATION,
    ModerationMode.AI_MODERATION_WITH_POST_ANYWAY,
    ModerationMode.MANUAL_MODERATION,
    ModerationMode.AI_AND_MANUAL_MODERATION,
];
/**
 * The mode a new space starts in, given the modes a silo permits: the first
 * permitted one in UI order.
 *
 * Falls back to AI moderation when the silo has no setting, and also when it
 * permits nothing at all — a space with no mode would go unmoderated, which is
 * the wrong way to fail.
 */
function getDefaultModerationMode(permittedModes) {
    const permitted = new Set(permittedModes ?? []);
    return exports.MODERATION_MODE_UI_ORDER.find((mode) => permitted.has(mode)) ?? ModerationMode.AI_MODERATION;
}
/**
 * Reads the active moderation mode out of an instance's feature flags.
 *
 * Returns `null` if no moderation feature is enabled — callers should treat
 * that as "no moderation" (the comment goes live with no inReview or
 * rejected flags).
 *
 * Resolved in `MODERATION_MODE_UI_ORDER`: an instance should never have two
 * modes enabled at once, but if it did, the earlier one wins.
 */
function getCurrentModerationSetting(instanceFeatures) {
    const enabled = new Set(Object.entries(instanceFeatures)
        .filter(([_, value]) => value)
        .map(([key]) => key));
    return exports.MODERATION_MODE_UI_ORDER.find((mode) => enabled.has(mode)) ?? null;
}
/**
 * The feature patch that makes `mode` the instance's active mode: that mode's
 * key on, the other three off, and `premoderation` on. The inverse of
 * `getCurrentModerationSetting`.
 *
 * `premoderation` rides along because the four mode features are registered as
 * its children (`COMPOSITE(PreModeratorFeature, ...)` in
 * `client/feature/question/question-module.ts`), and config assembly drops a
 * composite's children when the parent is off. Without it a silo that turns
 * `premoderation` off would lose every mode's UI — banners, post-action label,
 * the in-review modal — while the server kept moderating, since the comment
 * pipeline reads the mode keys and never reads `premoderation`. Unpicking that
 * coupling is tracked separately.
 */
function getFeaturesForModerationMode(mode) {
    const featureUpdate = { premoderation: true };
    Object.values(ModerationMode).forEach((featureKey) => {
        featureUpdate[featureKey] = mode === featureKey;
    });
    return featureUpdate;
}
//# sourceMappingURL=moderation-modes.js.map